import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, QrCode } from "lucide-react";

export default function QRRefRedirect() {
  const { qrRef } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/scan/q/${encodeURIComponent(qrRef)}`);
        const { resolved, target, candidates: cands } = res.data;
        if (resolved && target) {
          // Redirect direkte til det korrekte punkt
          navigate(`/scan/${target.point_id}`, { replace: true });
          return;
        }
        // Flere kandidater — brugeren skal vælge
        setCandidates(cands || []);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || "QR-kode kunne ikke findes");
        setLoading(false);
      }
    })();
  }, [qrRef, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="qr-ref-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Finder {qrRef}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="qr-ref-error">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">QR-kode ikke fundet</h2>
          <p className="text-sm text-muted-foreground mb-4">Referencen <span className="font-mono font-bold">{qrRef}</span> genkendes ikke.</p>
          <p className="text-xs text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="qr-ref-picker">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-6">
        <div className="text-center mb-4">
          <QrCode className="h-10 w-10 text-primary mx-auto mb-2" />
          <h2 className="text-xl font-bold">Vælg event</h2>
          <p className="text-sm text-muted-foreground">Flere events har <span className="font-mono font-bold">{qrRef}</span>. Hvilket arbejder du med?</p>
        </div>
        <div className="space-y-2">
          {candidates.map((c) => (
            <Button
              key={c.point_id}
              variant="outline"
              className="w-full justify-between h-auto py-3"
              onClick={() => navigate(`/scan/${c.point_id}`)}
              data-testid={`qr-ref-candidate-${c.point_id}`}
            >
              <div className="text-left">
                <div className="font-bold">{c.event_name}</div>
                <div className="text-xs text-muted-foreground">{c.name} · {c.kind === 'maxicontainer' ? 'Maxicontainer' : 'Ressourceø'}</div>
              </div>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
